import {PortalSectionNavigation} from './PortalSectionNavigation';
import {InboxCount} from './InboxCount';
import {Icon} from './Icon';
import {isAlphaPortalPath} from './alpha-portal-path';
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigationType } from 'react-router';
import {WorkflowReturn} from './WorkflowReturn';
import {BuildingSetupLayout} from './BuildingSetupLayout';
import { BuildingWorkspaceLayout } from './BuildingWorkspaceLayout';
import { globalDestinations,primarySection } from './workspace-navigation';
import { withBuildingScope } from './building-scope';
import { ConfirmationHost } from './ConfirmationHost';
import { StaffSessionProvider, useWorkspaceSession, useScopePending } from './StaffSession';
import { currentRole, type RoleMode } from '../api';
import { useAuth } from '../auth';
import { GlobalSearch } from './GlobalSearch';
import { State } from './State';
import {administrationSections} from '../views/administration-sections';
import {permittedWorkspaces} from './workspace-catalogue';
import {WorkspaceMenu} from './WorkspaceMenu';
import {BlockieAssistant} from './BlockieAssistant';

const staffIdentity = 'BlockGenie';

export function StaffIdentity({ linked = false, onDark = false, onNavigate }: { linked?: boolean; onDark?: boolean; onNavigate?: () => void }) {
  // The approved PDF uses the intact reversed artwork, including its original
  // proportions. Never reconstruct the genie and wordmark from separate crops.
  const artwork = <image href="/branding/blockgenie-approved.png" width="1774" height="887" />;
  const content = linked || onDark
    ? <svg className="brand-artwork brand-artwork--compact" viewBox="0 0 480 236" role="img" aria-label={staffIdentity}>
      <image href="/branding/blockgenie-reversed.png" width="480" height="236" />
    </svg>
    : <svg className="brand-artwork brand-artwork--full" viewBox="220 122 1405 652" role="img" aria-label={staffIdentity}>{artwork}</svg>;

  return linked
    ? <NavLink className="brand compact-brand" to="/" aria-label={`${staffIdentity} home`} onClick={onNavigate}>{content}</NavLink>
    : <div className={`brand auth-brand${onDark ? ' auth-brand--masthead' : ''}`}>{content}</div>;
}

function AuthenticationScreen({children}: {children: ReactNode}) {
  return <main className="auth-screen auth-screen--branded">
    <header className="auth-masthead"><StaffIdentity onDark />
</header>
    <div className="auth-layout">
      <section className="auth-introduction" aria-label="Welcome to BlockGenie">
        <p className="auth-promise">Your buildings.<br />Your work.<br />One clear place.</p>
        <p>Secure access for your management team.</p>
      </section>
      <div className="auth-card">
        {children}
        <div className="auth-guidance">
          <p>Need help? Contact your BlockGenie administrator.</p>
          <p>Using a shared device? Sign out when you finish.</p>
        </div>
      </div>
    </div>
  </main>;
}

const roles: Array<{ value: RoleMode; label: string }> = [
  { value: 'saif', label: 'Senior approver' },
  { value: 'hadeel', label: 'Implementation and onboarding' },
  { value: 'operations', label: 'Property management and operations' },
  { value: 'administrator', label: 'System administrator' },
  { value: 'auditor', label: 'Auditor, view only' },
];

function formatEnvironmentName(environment: string | undefined) {
  return (environment ?? 'development')
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

export function AppLayout() {
  const auth = useAuth();
  if (auth.loading) return <AuthenticationScreen><State
    kind="loading"
    tone="information"
    heading="Preparing your secure workspace"
    message="Loading your sign-in and environment settings."
    className="auth-state"
  />
</AuthenticationScreen>;
  if (auth.error) return <AuthenticationScreen><State
    kind={auth.error.code === 'service_unavailable' ? 'unavailable' : 'error'}
    tone={auth.error.code === 'service_unavailable' ? 'unavailable' : 'danger'}
    heading="Secure workspace is unavailable"
    message={auth.error.message}
    retry={{ onRetry: () => window.location.reload() }}
    {...(auth.error.supportReference ? { supportReference: auth.error.supportReference } : {})}
    className="auth-state"
    focusOnMount
  />
</AuthenticationScreen>;
  if (auth.authenticationRequired) return <AuthenticationScreen><State
    kind="unavailable"
    tone="warning"
    heading="Your session has ended"
    message="Sign in again to continue."
    primaryAction={{ label: 'Sign in again', onClick: () => void auth.signIn() }}
    {...(auth.authenticationRequired.supportReference ? { supportReference: auth.authenticationRequired.supportReference } : {})}
    className="auth-state"
    focusOnMount
  />
</AuthenticationScreen>;
  if (auth.config?.authMode === 'entra' && !auth.account) {
    return <AuthenticationScreen><State
      kind="empty"
      tone="information"
      heading="Sign in to BlockGenie"
      message="Use your work Microsoft account. Your permissions determine which buildings and records you can access."
      primaryAction={{ label: 'Sign in with Microsoft', onClick: () => void auth.signIn() }}
      className="auth-state"
    />
</AuthenticationScreen>;
  }

  return <StaffSessionProvider key={auth.account?.homeAccountId ?? currentRole()}><StaffShell />
</StaffSessionProvider>;
}

function StaffShell() {
  const location=useLocation();
  const navigationType=useNavigationType();
  const previousPage=useRef(location.pathname);
  useLayoutEffect(()=>{
    // Filters and intentional history restoration retain their own position.
    // Fresh page navigation starts above the building tabs, never at main's focus offset.
    if(previousPage.current!==location.pathname&&navigationType!=='POP'&&!location.hash){
      document.getElementById('main-content')?.focus({preventScroll:true});
      window.scrollTo({top:0,left:0,behavior:'instant'});
    }
    previousPage.current=location.pathname;
  },[location.pathname,location.hash,navigationType]);
  const session = useWorkspaceSession()!;
  const scopePending=useScopePending();
  const [menuOpen, setMenuOpen] = useState(false);
  const [blockieAvailable,setBlockieAvailable]=useState(false);
  const [blockieOpen,setBlockieOpen]=useState(false);
  const menuToggle = useRef<HTMLButtonElement>(null);
  const auth = useAuth();
  const role = currentRole();
  const pinKey='blockgenie-workspace-pins:'+(auth.account?.homeAccountId??role);
  const [savedPins,setSavedPins]=useState<string[]>(()=>{try{const value=JSON.parse(localStorage.getItem(pinKey)??'[]');return Array.isArray(value)?value.filter((item:unknown)=>typeof item==='string').slice(0,2):[]}catch{return []}});
  const availableWorkspaces=permittedWorkspaces(session),pins=savedPins.filter(code=>availableWorkspaces.some(item=>item.code===code));
  const togglePin=(code:string)=>{const next=pins.includes(code)?pins.filter(item=>item!==code):[...pins,code].slice(0,2);setSavedPins(next);try{localStorage.setItem(pinKey,JSON.stringify(next))}catch{/* Preference remains usable for this session. */}};
  // Audit F-059: a destination appears once. Tools that duplicate a main destination (Buildings)
  // are not repeated in the settings menu, and the account menu does not repeat settings or audit links.
  const mainHrefs=new Set(globalDestinations(session).map(item=>item.href.split('?')[0]));
  const settingsTools=administrationSections(session.capabilities,auth.config??{}).flatMap(section=>section.tools).filter((tool,index,all)=>!mainHrefs.has(tool.href.split('?')[0])&&all.findIndex(other=>other.href===tool.href)===index);
  // Owner, 5 Oct 2026: the account menu does not repeat the main navigation; it is only a fallback for people without the Buildings tab.
  const hasBuildingsTab=session.permittedDestinations.some(item=>item.id==='buildings');
  const settingsMenuHrefs=new Set(['/admin',...settingsTools.map(tool=>tool.href.split('?')[0])]);
  const developmentAuth = auth.config?.authMode !== 'entra';
  const environmentLabel = `${formatEnvironmentName(auth.config?.environment)} · PRE-RELEASE`;
  const contextLabel = auth.config?.dataMode === 'pilot' ? `${auth.config.pilotName ?? 'Client'} · Live pilot data` : auth.config?.dataMode === 'live' ? 'Live operational data' : 'Controlled demonstration data';
  const setRole = (value: RoleMode) => { localStorage.setItem('myfm-dev-role', value); window.location.reload(); };
  useEffect(() => {
    if (!menuOpen) return;
    const close = (event: KeyboardEvent) => { if (event.key === 'Escape') { setMenuOpen(false); menuToggle.current?.focus(); } };
    document.addEventListener('keydown', close);
    return () => document.removeEventListener('keydown', close);
  }, [menuOpen]);
  const closeMenu = () => setMenuOpen(false);
  const accountMenu=(
        <details className="user-menu"
          onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) event.currentTarget.open = false; }}
          onKeyDown={event => { if (event.key === 'Escape') { event.stopPropagation(); event.currentTarget.open = false; event.currentTarget.querySelector('summary')?.focus(); } }}>
          <summary aria-label="Account menu">

            <span className="account-avatar" aria-hidden="true">{session.displayName.split(/\s+/).filter(Boolean).slice(0,2).map(part=>part[0]).join('').toUpperCase()||'BG'}</span><span className="sr-only">{session.displayName}</span>
          </summary>
          <div><p>{session.displayName}</p><p>{session.displayPersona}</p><p className="account-environment">{environmentLabel} · {contextLabel}</p>
          {session.permittedDestinations.some(item=>['mail','calendar','documents'].includes(item.id))&&<details className="workspace-tools" onKeyDown={event=>{if(event.key==='Escape'){event.stopPropagation();event.currentTarget.open=false;event.currentTarget.querySelector('summary')?.focus();}}} onBlur={event=>{if(!event.currentTarget.contains(event.relatedTarget as Node|null))event.currentTarget.open=false;}}>
            <summary>Tools <Icon name="chevron-down"/>
</summary><div>{session.permittedDestinations.filter(item=>['mail','calendar','documents'].includes(item.id)).map(item=><Link key={item.id} to={withBuildingScope(item.href,session.effectiveScope.kind==='building'?session.effectiveScope.id:undefined)} onClick={event=>{event.currentTarget.closest('details')?.removeAttribute('open');closeMenu();}}>{item.id==='mail'?'Inbox':item.label}</Link>)}</div>
          </details>}
            {settingsTools.length>0&&session.capabilities.includes('administration:view')?<NavLink to="/admin">Administration</NavLink>:settingsTools.map(tool=><NavLink key={tool.href} to={tool.href}>{tool.title}</NavLink>)}
            {session.permittedDestinations.filter(item=>item.id==='operating-appointments').map(item=><NavLink key={item.id} to={item.href} onClick={event=>event.currentTarget.closest('details')?.removeAttribute('open')}>{item.label}</NavLink>)}
            {!hasBuildingsTab && session.permittedDestinations.filter(item => ['people', 'mail', 'documents', 'audit', 'settings'].includes(item.id) && !(settingsTools.length>0 && settingsMenuHrefs.has(item.href.split('?')[0])))
              .map(item => <NavLink key={item.id} to={withBuildingScope(item.href,session.effectiveScope.kind==='building'&&!['settings','audit','enquiries'].includes(item.id)?session.effectiveScope.id:undefined)} onClick={event => { event.currentTarget.closest('details')?.removeAttribute('open'); closeMenu(); }}>{item.label}</NavLink>)}
            {!hasBuildingsTab && session.permittedDestinations.filter(item => ['work', 'finance', 'compliance', 'calendar'].includes(item.id)).map(item => <NavLink key={item.id} to={withBuildingScope(item.href,session.effectiveScope.kind==='building'&&!['settings','audit','enquiries'].includes(item.id)?session.effectiveScope.id:undefined)} onClick={event => event.currentTarget.closest('details')?.removeAttribute('open')}>{item.label}</NavLink>)}
            {developmentAuth
            ? <div className="dev-role">
                <label htmlFor="role">Development role</label>
                <select id="role" value={role} onChange={(event) => setRole(event.target.value as RoleMode)}>
                  {roles.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                </select>
                <small>Development-only identity simulation.</small>
              </div>
            : <button className="secondary" onClick={() => void auth.signOut()}>Sign out</button>}
          </div>
        </details>
  );
  if(isAlphaPortalPath(location.pathname))return <div className="alpha-portal-shell"><a className="skip-link" href="#main-content">Skip to portal content</a><header className="portal-masthead"><StaffIdentity onDark/>
<nav aria-label="Portal">{auth.config?.externalPortalEnabled===true&&<><Link to={auth.config?.alphaFeaturesEnabled?'/alpha':'/portal'}>Portal home</Link>{auth.config?.alphaFeaturesEnabled&&<Link to="/portal/home">Your home</Link>}</>}<button className="secondary" onClick={()=>void auth.signOut()}>Sign out</button></nav></header>
<div className="portal-content-column"><PortalSectionNavigation/>
<main id="main-content" tabIndex={-1}><Outlet/>
</main></div>
</div>;
  return <div className="app-shell top-navigation-shell">
    <a className="skip-link" href="#main-content">Skip to main content</a>
    <header className="app-header">
      <div className="app-header-row">
        <div className="app-brand-block">
          <StaffIdentity linked onNavigate={closeMenu} />

        </div>
        <button
          ref={menuToggle}
          type="button"
          className="nav-menu-toggle"
          aria-expanded={menuOpen}
          aria-controls="main-navigation"
          onClick={() => setMenuOpen((open) => !open)}
        >
          <Icon name={menuOpen?'x':'layout-list'} size={20}/>
          <span>{menuOpen ? 'Close' : 'Menu'}</span>
        </button>
        <nav
          id="main-navigation"
          className={`primary-nav horizontal-nav ${menuOpen ? 'open' : ''}`}
          aria-label="Main navigation"
        >
          {globalDestinations(session).filter(item=>item.id!=='engines').map(item => <Link key={item.id} to={item.href} state={{explicitWorkspace:true}} className={primarySection(location.pathname,location.search)===item.id?'active':undefined} aria-current={primarySection(location.pathname,location.search)===item.id?'page':undefined} onClick={closeMenu}><Icon name={item.id==='buildings'?'building':'house'}/>{item.label}</Link>)}
          <WorkspaceMenu session={session} onNavigate={closeMenu} active={primarySection(location.pathname,location.search)==='engines'} pins={pins} onPin={togglePin}/>
          {pins.map(code=>availableWorkspaces.find(item=>item.code===code)).filter(item=>item!==undefined).map(item=><Link key={item.code} className="workspace-pin" to={item.href} state={{explicitWorkspace:true}} onClick={closeMenu}>{item.name}</Link>)}
          {session.capabilities.includes('communications:view')&&<Link className="workspace-inbox-pin" aria-label="Inbox" aria-describedby="staff-inbox-count" to={withBuildingScope('/communications',session.effectiveScope.kind==='building'?session.effectiveScope.id:undefined)} onClick={closeMenu}><Icon name="inbox"/>Inbox<InboxCount building={session.effectiveScope.kind==='building'?session.effectiveScope.id:undefined}/>
</Link>}

        </nav>
        <div className="global-search-slot">{session.capabilities.includes('contacts:search') && <GlobalSearch key={JSON.stringify([session.effectiveScope,session.capabilities])} />}</div>
        <div className="account-tools">
        <button id="ask-blockie-trigger" type="button" className="ask-blockie" aria-expanded={blockieOpen} aria-controls={blockieOpen?'blockie-panel':undefined} disabled={!blockieAvailable} title={blockieAvailable?'Ask Genie about this workspace':'Genie is unavailable for this account or environment'} onClick={()=>{closeMenu();window.dispatchEvent(new Event('blockgenie:ask-blockie'));}} aria-label="Ask Genie"><Icon name="sparkles" size={20}/>
</button>

        </div>
      </div>
    </header>
    <BlockieAssistant headerTrigger onAvailability={setBlockieAvailable} onOpenChange={setBlockieOpen}/>
<main id="main-content" tabIndex={-1}><div className="content">{scopePending?<State kind="loading" tone="information" heading="Loading building workspace" message="Checking access to the selected building."/>:<BuildingWorkspaceLayout account={accountMenu}><BuildingSetupLayout><WorkflowReturn/>
<Outlet />
</BuildingSetupLayout></BuildingWorkspaceLayout>}</div>
</main><ConfirmationHost/>
  </div>;
}
